import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import './YoutubeScriptEditor.css';

const YoutubeScriptEditor = () => {
  const { scriptId } = useParams();
  const navigate = useNavigate();
  const [script, setScript] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editedScript, setEditedScript] = useState(null);
  const [activeTab, setActiveTab] = useState('script'); // 'script', 'titles', 'reels'

  useEffect(() => {
    loadScript();
  }, [scriptId]);

  const loadScript = async () => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        navigate('/login');
        return;
      }

      const db = getFirestore();
      const scriptRef = doc(db, 'content', user.uid, 'youtube-scripts', scriptId);
      const scriptDoc = await getDoc(scriptRef);

      if (scriptDoc.exists()) {
        const data = { id: scriptDoc.id, ...scriptDoc.data() };
        setScript(data);
        setEditedScript(data);
      } else {
        alert('Script not found');
        navigate('/generate');
      }
    } catch (error) {
      console.error('Error loading script:', error);
      alert('Failed to load script');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      const db = getFirestore();
      
      const scriptRef = doc(db, 'content', user.uid, 'youtube-scripts', scriptId);
      await updateDoc(scriptRef, {
        title: editedScript.title,
        hook: editedScript.hook,
        fullScript: editedScript.fullScript,
        updatedAt: new Date()
      });

      setScript(editedScript);
      alert('✅ Script saved successfully!');
    } catch (error) {
      console.error('Error saving script:', error);
      alert('Failed to save script');
    } finally {
      setIsSaving(false);
    }
  };

  const handleExport = () => {
    const blob = new Blob([editedScript.fullScript], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${editedScript.title}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyScript = () => {
    navigator.clipboard.writeText(editedScript.fullScript);
    alert('✅ Script copied to clipboard!');
  };

  if (isLoading) {
    return (
      <div className="yt-script-editor-loading">
        <div className="yt-loading-spinner"></div>
        <p>Loading script...</p>
      </div>
    );
  }

  if (!script) {
    return (
      <div className="yt-script-editor-error">
        <p>Script not found</p>
        <button onClick={() => navigate('/generate')}>
          ← Back to Generate
        </button>
      </div>
    );
  }

  return (
    <div className="yt-script-editor-container">
      {/* Header */}
      <div className="yt-script-editor-header">
        <button 
          className="yt-back-btn"
          onClick={() => navigate('/generate')}
        >
          ← Back
        </button>
        <div className="yt-header-info">
          <h1 className="yt-script-editor-title">{editedScript.title}</h1>
          <div className="yt-script-meta">
            <span className="yt-variant-badge">Script {script.variant}</span>
            <span className="yt-meta-item">⏱️ {script.estimatedDuration}</span>
            <span className="yt-meta-item">📝 {script.wordCount} words</span>
            {script.reelSegments && script.reelSegments.length > 0 && (
              <span className="yt-meta-item">📱 {script.reelSegments.length} reels</span>
            )}
          </div>
        </div>
        <div className="yt-header-actions">
          <button 
            className="yt-btn-secondary"
            onClick={handleCopyScript}
          >
            📋 Copy
          </button>
          <button 
            className="yt-btn-secondary"
            onClick={handleExport}
          >
            💾 Export
          </button>
          <button 
            className="yt-btn-primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? '💾 Saving...' : '💾 Save Changes'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="yt-script-tabs">
        <button 
          className={`yt-tab ${activeTab === 'script' ? 'active' : ''}`}
          onClick={() => setActiveTab('script')}
        >
          📝 Script
        </button>
        <button 
          className={`yt-tab ${activeTab === 'titles' ? 'active' : ''}`}
          onClick={() => setActiveTab('titles')}
        >
          🎨 Title Ideas ({script.titleIdeas?.length || 0})
        </button>
        {script.reelSegments && script.reelSegments.length > 0 && (
          <button 
            className={`yt-tab ${activeTab === 'reels' ? 'active' : ''}`}
            onClick={() => setActiveTab('reels')}
          >
            📱 Reel Segments ({script.reelSegments.length})
          </button>
        )}
      </div>

      {/* Content */}
      <div className="yt-script-content">
        
        {/* Script Tab */}
        {activeTab === 'script' && (
          <div className="yt-script-tab-content">
            {/* Hook Section */}
            <div className="yt-editor-section">
              <label className="yt-editor-label">
                🎣 Hook (First 15 seconds)
              </label>
              <textarea
                className="yt-editor-hook"
                value={editedScript.hook}
                onChange={(e) => setEditedScript({ ...editedScript, hook: e.target.value })}
                rows={3}
              />
            </div>

            {/* Full Script Section */}
            <div className="yt-editor-section">
              <label className="yt-editor-label">
                📜 Full Script
              </label>
              <textarea
                className="yt-editor-script"
                value={editedScript.fullScript}
                onChange={(e) => setEditedScript({ ...editedScript, fullScript: e.target.value })}
                rows={30}
              />
            </div>
          </div>
        )}

        {/* Titles Tab */}
        {activeTab === 'titles' && (
          <div className="yt-titles-tab-content">
            <div className="yt-titles-intro">
              <h3>🎨 20 Alternative Title Ideas</h3>
              <p>Ultra-clickable titles with psychological triggers. Click to copy!</p>
            </div>
            <div className="yt-titles-grid">
              {script.titleIdeas && script.titleIdeas.map((idea) => (
                <div 
                  key={idea.id} 
                  className={`yt-title-card yt-title-${idea.category}`}
                  onClick={() => {
                    navigator.clipboard.writeText(idea.title);
                    alert('✅ Title copied!');
                  }}
                >
                  <div className="yt-title-header">
                    <span className="yt-title-id">#{idea.id}</span>
                    <span className={`yt-title-category-badge yt-category-${idea.category}`}>
                      {idea.category}
                    </span>
                  </div>
                  <div className="yt-title-text">{idea.title}</div>
                  <div className="yt-title-why">{idea.why}</div>
                  <div className="yt-title-copy-hint">Click to copy 📋</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reels Tab */}
        {activeTab === 'reels' && script.reelSegments && (
          <div className="yt-reels-tab-content">
            <div className="yt-reels-intro">
              <h3>📱 Reel-Ready Segments</h3>
              <p>Each segment works as a standalone reel with strong hook & body</p>
            </div>
            <div className="yt-reels-list">
              {script.reelSegments.map((segment, idx) => (
                <div key={idx} className="yt-reel-segment">
                  <div className="yt-reel-header">
                    <span className="yt-reel-number">Reel #{segment.segmentNumber || idx + 1}</span>
                    <span className="yt-reel-duration">{segment.estimatedDuration || '~30s'}</span>
                  </div>
                  <div className="yt-reel-hook">
                    <strong>🎣 Hook:</strong> {segment.hook}
                  </div>
                  <div className="yt-reel-body">
                    {segment.body}
                  </div>
                  <button 
                    className="yt-reel-copy-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(`${segment.hook}\n\n${segment.body}`);
                      alert('✅ Reel copied!');
                    }}
                  >
                    📋 Copy Reel
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default YoutubeScriptEditor;

